/**
 * Map Composition MCP tools — CRUD, slot management, versioning, and suggestion.
 *
 * Maps are composed frameworks (e.g. Lean Canvas) assembled from shared
 * ingredient entries across collections. Each slot holds references to
 * ingredients; committing pins versions to create an immutable snapshot.
 */

import { McpServer } from "@modelcontextprotocol/sdk/server/mcp.js";
import { z } from "zod/v3";
import { kernelQuery, kernelMutation, getWorkspaceContext, runWithToolContext } from "../client.js";
import { trackWriteTool } from "../tool-surface.js";
import { thinWrapper, success, failure, validationResult, unknownAction, parseOrFail, type ToolResult } from "../envelope.js";
import { toVersionDisplay } from "../lib/versionDisplay.js";
import { dispatchDiscriminated, type ActionParamSpec } from "../compound-tool.js";

export const createAudienceMapSetSchema = z.object({
  audienceEntryId: z.string().max(200).describe("Entry ID of the audience (e.g. STR-fb7hje)"),
});

export const mapSchema = z.object({
  action: z
    .enum(["create", "get", "list"])
    .describe("Action: create a map, get map details, or list all maps"),
  mapEntryId: z.string().max(200).optional().describe("Map entry ID (for get)"),
  title: z.string().max(500).optional().describe("Map title (for create)"),
  templateId: z
    .string()
    .max(200)
    .optional()
    .default("lean-canvas")
    .describe("Template slug for create: 'lean-canvas' or any composed template"),
  description: z.string().max(20000).optional().describe("Description (for create)"),
  slotIds: z
    .array(z.string().max(200))
    .max(200)
    .optional()
    .describe("Slot IDs to initialize (for create; auto-populated from template if omitted)"),
  status: z.string().max(200).optional().describe("Filter by status for list"),
});

export const mapSlotSchema = z.object({
  action: z
    .enum(["add", "remove", "replace", "list"])
    .describe("Action: add/remove/replace an ingredient in a slot, or list slot contents"),
  mapEntryId: z.string().max(200).describe("Map entry ID"),
  slotId: z.string().max(200).optional().describe("Slot ID (e.g. 'problem', 'customer-segments')"),
  ingredientEntryId: z
    .string()
    .max(200)
    .optional()
    .describe("Ingredient entry ID to add/remove"),
  newIngredientEntryId: z
    .string()
    .max(200)
    .optional()
    .describe("New ingredient entry ID (for replace)"),
  label: z.string().max(500).optional().describe("Display label override"),
  author: z.string().max(200).optional().describe("Who is performing the action"),
});

export const mapVersionSchema = z.object({
  action: z
    .enum(["commit", "list", "history"])
    .describe("Action: commit the map, list commits, or view commit history"),
  mapEntryId: z.string().max(200).describe("Map entry ID"),
  commitMessage: z
    .string()
    .max(2000)
    .optional()
    .describe("Commit message (for commit action)"),
  author: z.string().max(200).optional().describe("Who is committing"),
});

export const mapSuggestSchema = z.object({
  mapEntryId: z.string().max(200).describe("Map entry ID to suggest ingredients for"),
  slotId: z
    .string()
    .max(200)
    .optional()
    .describe("Specific slot to find ingredients for (or all empty slots)"),
  query: z
    .string()
    .max(500)
    .optional()
    .describe("Optional search query to narrow ingredient suggestions"),
});

function slotSummary(slots: Record<string, any[]>): string {
  return Object.entries(slots)
    .map(([id, refs]) => {
      if (!refs || refs.length === 0) return `  - **${id}**: (empty)`;
      const items = refs
        .map((r: any) => {
          const name = r.ingredientName ?? r.label ?? r.entryId;
          const version = r.pinnedVersion ? ` v${r.pinnedVersion}` : "";
          const status = r.ingredientStatus ? ` [${r.ingredientStatus}]` : "";
          return `${name}${version}${status}`;
        })
        .join(", ");
      return `  - **${id}**: ${items}`;
    })
    .join("\n");
}

/**
 * WP-484 S2 (§3): extracted handler — reused by the legacy
 * `create-audience-map-set` tool AND the new `map` compound tool's
 * `create-audience-set` action. One business-logic path.
 */
export async function handleCreateAudienceMapSet({ audienceEntryId }: z.infer<typeof createAudienceMapSetSchema>) {
  const result = await kernelMutation<{ audienceEntryId: string; maps: Array<{ templateId: string; mapEntryId: string }> }>(
    "maps.createAudienceMapSet",
    { audienceEntryId }
  );

  const wsCtx = await getWorkspaceContext();
  const lines = result.maps.map(
    (m) => `- **${m.templateId}**: \`${m.mapEntryId}\``
  );
  return {
    content: [
      {
        type: "text" as const,
        text:
          `# Audience Map Set Created\n\n` +
          `**Audience:** \`${result.audienceEntryId}\`\n` +
          `**Workspace:** ${wsCtx.workspaceSlug}\n\n` +
          `## Maps\n\n${lines.join("\n")}\n\n` +
          `Use map action=slot.add to add ingredients. View at /empathy, /narrowing, and /jobs.`,
      },
    ],
    structuredContent: success(
      `Created ${result.maps.length} audience maps for ${result.audienceEntryId}.`,
      { audienceEntryId: result.audienceEntryId, maps: result.maps },
      result.maps.map((m) => ({
        tool: "map",
        description: `Add ingredients to ${m.templateId}`,
        parameters: { action: "slot.add", mapEntryId: m.mapEntryId },
      })),
    ),
  };
}

/**
 * WP-484 S2 (§3): extracted CRUD handler — reused by the legacy `map` tool
 * AND the new `map` compound tool's unnamespaced create/get/list actions.
 * One business-logic path.
 */
export async function handleMapCrud({ action, mapEntryId, title, templateId, description, slotIds, status }: z.infer<typeof mapSchema>) {
      if (action === "create") {
        if (!title) {
          return validationResult("A `title` is required to create a map.");
        }

        const result = await kernelMutation<{ _id: string; entryId: string }>(
          "maps.createMap",
          { title, templateId, description, slotIds }
        );

        const wsCtx = await getWorkspaceContext();
        return {
          content: [
            {
              type: "text" as const,
              text:
                `# Map Created\n\n` +
                `- **Entry ID:** \`${result.entryId}\`\n` +
                `- **Title:** ${title}\n` +
                `- **Template:** ${templateId}\n` +
                `- **Status:** draft\n` +
                `- **Workspace:** ${wsCtx.workspaceSlug} (${wsCtx.workspaceId})\n\n` +
                `Use \`map action=slot.add mapEntryId="${result.entryId}" slotId="problem" ingredientEntryId="..."\` to start filling slots.`,
            },
          ],
          structuredContent: success(
            `Created map '${title}' (${result.entryId}) using ${templateId} template.`,
            { entryId: result.entryId, title, templateId, status: "draft" },
            [{ tool: "map", description: "Add ingredient to slot", parameters: { action: "slot.add", mapEntryId: result.entryId } }],
          ),
        };
      }

      if (action === "get") {
        if (!mapEntryId) {
          return validationResult("A `mapEntryId` is required.");
        }

        const map = await kernelQuery<any>("maps.getMap", { mapEntryId });
        if (!map) {
          return {
            content: [{ type: "text" as const, text: `Map "${mapEntryId}" not found.` }],
            structuredContent: failure(
              "NOT_FOUND",
              `Map "${mapEntryId}" not found.`,
              "Use map action=list to find available maps.",
              [{ tool: "map", description: "List maps", parameters: { action: "list" } }],
            ),
          };
        }

        return {
          content: [
            {
              type: "text" as const,
              text:
                `# ${map.name}\n\n` +
                `- **Entry ID:** \`${map.entryId}\`\n` +
                `- **Template:** ${map.templateName}\n` +
                `- **Status:** ${map.status}\n` +
                `- **Version:** ${toVersionDisplay(map.currentVersion)}\n` +
                `- **Completion:** ${map.completionScore}% (${map.filledSlots}/${map.totalSlots} slots)\n\n` +
                `## Slots\n\n` +
                slotSummary(map.slots),
            },
          ],
          structuredContent: success(
            `${map.name}: ${map.completionScore}% complete (${map.filledSlots}/${map.totalSlots} slots), ${toVersionDisplay(map.currentVersion)}.`,
            { entryId: map.entryId, name: map.name, templateName: map.templateName, status: map.status, completionScore: map.completionScore, filledSlots: map.filledSlots, totalSlots: map.totalSlots, version: map.currentVersion ?? null, versionDisplay: toVersionDisplay(map.currentVersion) },
            [{ tool: "map", description: "Manage slot ingredients", parameters: { action: "slot.list", mapEntryId } }],
          ),
        };
      }

      if (action === "list") {
        const maps = await kernelQuery<any[]>("maps.listMaps", {
          templateId: templateId !== "lean-canvas" ? templateId : undefined,
          status,
        });

        if (!maps || maps.length === 0) {
          return {
            content: [{ type: "text" as const, text: "No maps found. Create one with `map action=create title=\"My Canvas\"`." }],
            structuredContent: success(
              "No maps found.",
              { maps: [], total: 0 },
              [{ tool: "map", description: "Create a map", parameters: { action: "create" } }],
            ),
          };
        }

        const lines = maps.map(
          (m: any) =>
            `- **${m.name}** (\`${m.entryId}\`) — ${m.templateId}, ${m.completionScore}% complete, ${toVersionDisplay(m.currentVersion)}`
        );

        return {
          content: [{ type: "text" as const, text: `# Maps (${maps.length})\n\n${lines.join("\n")}` }],
          structuredContent: success(
            `Found ${maps.length} map(s).`,
            { maps: maps.map((m: any) => ({ entryId: m.entryId, name: m.name, templateId: m.templateId, completionScore: m.completionScore })), total: maps.length },
          ),
        };
      }

      return unknownAction(action, ["create", "get", "list"]);
}

/**
 * WP-484 S2 (§3): extracted slot handler — reused by the legacy `map-slot`
 * tool AND the new `map` compound tool's `slot.*` namespaced actions. One
 * business-logic path.
 */
export async function handleMapSlot({
  action,
  mapEntryId,
  slotId,
  ingredientEntryId,
  newIngredientEntryId,
  label,
  author,
}: z.infer<typeof mapSlotSchema>) {
      if (action === "list") {
        const map = await kernelQuery<any>("maps.getMap", { mapEntryId });
        if (!map) {
          return {
            content: [{ type: "text" as const, text: `Map "${mapEntryId}" not found.` }],
            structuredContent: failure(
              "NOT_FOUND",
              `Map "${mapEntryId}" not found.`,
              "Use map action=list to find available maps.",
              [{ tool: "map", description: "List maps", parameters: { action: "list" } }],
            ),
          };
        }

        if (slotId && map.slots[slotId]) {
          const refs = map.slots[slotId];
          return {
            content: [
              {
                type: "text" as const,
                text:
                  `# Slot: ${slotId}\n\n` +
                  (refs.length === 0
                    ? "(empty)"
                    : refs
                        .map(
                          (r: any) =>
                            `- **${r.ingredientName ?? r.entryId}** (\`${r.entryId}\`)${r.pinnedVersion ? ` v${r.pinnedVersion}` : ""}`
                        )
                        .join("\n")),
              },
            ],
            structuredContent: success(
              refs.length === 0 ? `Slot "${slotId}" is empty.` : `Slot "${slotId}": ${refs.length} ingredient(s).`,
              { mapEntryId, slotId, ingredients: refs, count: refs.length },
            ),
          };
        }

        return {
          content: [{ type: "text" as const, text: `## All Slots\n\n${slotSummary(map.slots)}` }],
          structuredContent: success(
            `${map.name}: ${Object.keys(map.slots).length} slots.`,
            { mapEntryId, slotCount: Object.keys(map.slots).length },
          ),
        };
      }

      if (action === "add") {
        if (!slotId || !ingredientEntryId) {
          return validationResult("Both `slotId` and `ingredientEntryId` are required for add.");
        }

        await kernelMutation("maps.addToSlot", { mapEntryId, slotId, ingredientEntryId, label, author });

        return {
          content: [{ type: "text" as const, text: `Added \`${ingredientEntryId}\` to slot "${slotId}" on map \`${mapEntryId}\`.` }],
          structuredContent: success(
            `Added ${ingredientEntryId} to slot "${slotId}".`,
            { mapEntryId, slotId, ingredientEntryId },
            [{ tool: "map", description: "View updated map", parameters: { action: "get", mapEntryId } }],
          ),
        };
      }

      if (action === "remove") {
        if (!slotId || !ingredientEntryId) {
          return validationResult("Both `slotId` and `ingredientEntryId` are required for remove.");
        }

        await kernelMutation("maps.removeFromSlot", { mapEntryId, slotId, ingredientEntryId, author });

        return {
          content: [{ type: "text" as const, text: `Removed \`${ingredientEntryId}\` from slot "${slotId}" on map \`${mapEntryId}\`.` }],
          structuredContent: success(
            `Removed ${ingredientEntryId} from slot "${slotId}".`,
            { mapEntryId, slotId, ingredientEntryId },
            [{ tool: "map", description: "View updated map", parameters: { action: "get", mapEntryId } }],
          ),
        };
      }

      if (action === "replace") {
        if (!slotId || !ingredientEntryId || !newIngredientEntryId) {
          return validationResult("`slotId`, `ingredientEntryId`, and `newIngredientEntryId` are required for replace.");
        }

        await kernelMutation("maps.replaceInSlot", { mapEntryId, slotId, oldIngredientEntryId: ingredientEntryId, newIngredientEntryId, label, author });

        return {
          content: [{ type: "text" as const, text: `Replaced \`${ingredientEntryId}\` with \`${newIngredientEntryId}\` in slot "${slotId}".` }],
          structuredContent: success(
            `Replaced ${ingredientEntryId} with ${newIngredientEntryId} in slot "${slotId}".`,
            { mapEntryId, slotId, removed: ingredientEntryId, added: newIngredientEntryId },
            [{ tool: "map", description: "View updated map", parameters: { action: "get", mapEntryId } }],
          ),
        };
      }

      return unknownAction(action, ["add", "remove", "replace", "list"]);
}

/**
 * WP-484 S2 (§3): extracted version handler — reused by the legacy
 * `map-version` tool AND the new `map` compound tool's `version.*`
 * namespaced actions. One business-logic path.
 */
export async function handleMapVersion({ action, mapEntryId, commitMessage, author }: z.infer<typeof mapVersionSchema>) {
      if (action === "commit") {
        const result = await kernelMutation<any>("maps.commitMap", {
          mapEntryId,
          commitMessage: commitMessage ?? "Map committed",
          author,
        });

        return {
          content: [
            {
              type: "text" as const,
              text:
                `# Map Committed\n\n` +
                `- **Version:** ${result.version}\n` +
                `- **Completion:** ${result.completionScore}%\n` +
                `- **Slots modified:** ${result.slotsModified.length > 0 ? result.slotsModified.join(", ") : "(none)"}\n\n` +
                `All ingredient references have been pinned at their current versions.`,
            },
          ],
          structuredContent: success(
            `Committed map ${mapEntryId} at v${result.version} (${result.completionScore}% complete).`,
            { mapEntryId, version: result.version, versionDisplay: toVersionDisplay(result.version), completionScore: result.completionScore, slotsModified: result.slotsModified },
          ),
        };
      }

      if (action === "list" || action === "history") {
        const commits = await kernelQuery<any[]>("maps.listMapCommits", { mapEntryId });

        if (!commits || commits.length === 0) {
          return {
            content: [{ type: "text" as const, text: `No commits yet for map \`${mapEntryId}\`. Use \`map action=version.commit\` to create the first snapshot.` }],
            structuredContent: success(
              `No commits yet for ${mapEntryId}.`,
              { mapEntryId, commits: [], total: 0 },
              [{ tool: "map", description: "Commit the map", parameters: { action: "version.commit", mapEntryId } }],
            ),
          };
        }

        const lines = commits.map(
          (c: any) =>
            `- **v${c.version}** (${new Date(c.createdAt).toLocaleDateString()}) — ${c.commitMessage ?? "(no message)"} — by ${c.author}` +
            (c.slotsModified.length > 0 ? `\n  Slots changed: ${c.slotsModified.join(", ")}` : "")
        );

        return {
          content: [{ type: "text" as const, text: `# Map History: \`${mapEntryId}\` (${commits.length} commits)\n\n${lines.join("\n")}` }],
          structuredContent: success(
            `${commits.length} commit(s) for ${mapEntryId}.`,
            { mapEntryId, total: commits.length },
          ),
        };
      }

      return unknownAction(action, ["commit", "list", "history"]);
}

/**
 * WP-484 S2 (§3): extracted suggest handler — reused by the legacy
 * `map-suggest` tool AND the new `map` compound tool's `suggest` action.
 * One business-logic path.
 */
export async function handleMapSuggest({ mapEntryId, slotId, query }: z.infer<typeof mapSuggestSchema>) {
      const map = await kernelQuery<any>("maps.getMap", { mapEntryId });
      if (!map) {
        return {
          content: [{ type: "text" as const, text: `Map "${mapEntryId}" not found.` }],
          structuredContent: failure(
            "NOT_FOUND",
            `Map "${mapEntryId}" not found.`,
            "Use map action=list to find available maps.",
            [{ tool: "map", description: "List maps", parameters: { action: "list" } }],
          ),
        };
      }

      const emptySlots = slotId
        ? [slotId].filter((id) => (map.slots[id]?.length ?? 0) === 0)
        : Object.entries(map.slots as Record<string, any[]>)
            .filter(([, refs]) => refs.length === 0)
            .map(([id]) => id);

      if (emptySlots.length === 0) {
        const msg = slotId
          ? `Slot "${slotId}" already has ingredients.`
          : "All slots have ingredients. Nothing to suggest.";
        return {
          content: [{ type: "text" as const, text: msg }],
          structuredContent: success(msg, { mapEntryId, emptySlots: 0 }),
        };
      }

      const slotDefs = map.slotDefs ?? [];
      const suggestions: string[] = [];

      for (const sid of emptySlots) {
        const def = slotDefs.find((s: any) => s.id === sid);
        const searchQuery = query ?? def?.label ?? sid;

        const results = await kernelQuery<any[]>("knowledge.search", {
          query: searchQuery,
          limit: 5,
        });

        if (results && results.length > 0) {
          const items = results.map(
            (r: any) =>
              `  - **${r.name}** (\`${r.entryId}\`, ${r.collectionName ?? "unknown"}) — ${r.status}`
          );
          suggestions.push(
            `### ${def?.label ?? sid}\n${def?.description ?? ""}\n\n${items.join("\n")}`
          );
        } else {
          suggestions.push(
            `### ${def?.label ?? sid}\n_No matching entries found. Create ingredients first._`
          );
        }
      }

      return {
        content: [
          {
            type: "text" as const,
            text:
              `# Ingredient Suggestions for "${map.name}"\n\n` +
              suggestions.join("\n\n") +
              `\n\nUse \`map action=slot.add mapEntryId="${mapEntryId}" slotId="..." ingredientEntryId="..."\` to add ingredients.`,
          },
        ],
        structuredContent: success(
          `Suggestions for ${emptySlots.length} empty slot(s) on "${map.name}".`,
          { mapEntryId, emptySlots: emptySlots.length },
          [{ tool: "map", description: "Add ingredient", parameters: { action: "slot.add", mapEntryId } }],
        ),
      };
}

// ─── `map` compound tool (WP-484 S2, §3 module-gated table) ───────────────
// Absorbs `map-slot` → slot.*, `map-version` → version.*, `map-suggest` →
// suggest, and `create-audience-map-set` → create-audience-set. The `map`
// name is REUSED (not "alongside"-eligible); the four OTHER legacy names
// stay registered separately (§7 "alongside" mechanic below). 12 actions —
// exactly at the §3 per-noun ceiling.
const MAP_ACTIONS = [
  "create", "get", "list",
  "slot.add", "slot.remove", "slot.replace", "slot.list",
  "version.commit", "version.list", "version.history",
  "suggest", "create-audience-set",
] as const;
type MapAction = (typeof MAP_ACTIONS)[number];

// §3a — the REGISTERED layer: flat ZodObject, action enum + superset of every
// absorbed action's params (deduped across mapSchema/mapSlotSchema/
// mapVersionSchema/mapSuggestSchema/createAudienceMapSetSchema).
export const mapCompoundSchema = z.object({
  action: z.enum(MAP_ACTIONS).describe(
    "Unnamespaced: 'create'/'get'/'list' — map CRUD (the original `map` tool). " +
      "'slot.*' (add/remove/replace/list) — ingredient slot management, absorbs `map-slot`. " +
      "'version.*' (commit/list/history) — versioning, absorbs `map-version`. " +
      "'suggest' — find ingredients to fill empty slots, absorbs `map-suggest`. " +
      "'create-audience-set' — create all three audience intelligence maps at once, absorbs `create-audience-map-set`.",
  ),
  mapEntryId: z.string().max(200).optional().describe("Map entry ID. Required for get and all slot.*/version.*/suggest actions."),
  title: z.string().max(500).optional().describe("For 'create': map title (required)."),
  templateId: z.string().max(200).optional().default("lean-canvas").describe("For 'create': template slug."),
  description: z.string().max(20000).optional().describe("For 'create': description."),
  slotIds: z.array(z.string().max(200)).max(200).optional().describe("For 'create': slot IDs to initialize."),
  status: z.string().max(200).optional().describe("For 'list': filter by status."),
  slotId: z.string().max(200).optional().describe("For 'slot.add'/'slot.remove'/'slot.replace': slot ID (required). For 'suggest': specific slot (optional)."),
  ingredientEntryId: z.string().max(200).optional().describe("For 'slot.add'/'slot.remove'/'slot.replace': ingredient entry ID to add/remove (required)."),
  newIngredientEntryId: z.string().max(200).optional().describe("For 'slot.replace': new ingredient entry ID (required)."),
  label: z.string().max(500).optional().describe("For 'slot.add'/'slot.replace': display label override."),
  author: z.string().max(200).optional().describe("Who is performing the action."),
  commitMessage: z.string().max(2000).optional().describe("For 'version.commit': commit message."),
  query: z.string().max(500).optional().describe("For 'suggest': search query to narrow ingredient suggestions."),
  audienceEntryId: z.string().max(200).optional().describe("For 'create-audience-set': audience entry ID (required)."),
});

// §3b — the HANDLER layer: per-action discriminatedUnion, reusing the
// existing legacy schemas (Code Integrity) via `.omit({action:true}).extend(...)`.
const mapCreateVariant = mapSchema.omit({ action: true }).extend({ action: z.literal("create") });
const mapGetVariant = z.object({ action: z.literal("get"), mapEntryId: z.string().max(200) });
const mapListVariant = z.object({ action: z.literal("list"), templateId: z.string().max(200).optional(), status: z.string().max(200).optional() });
const slotBase = mapSlotSchema.omit({ action: true });
const mapSlotAddVariant = slotBase.extend({ action: z.literal("slot.add"), slotId: z.string().max(200), ingredientEntryId: z.string().max(200) });
const mapSlotRemoveVariant = slotBase.extend({ action: z.literal("slot.remove"), slotId: z.string().max(200), ingredientEntryId: z.string().max(200) });
const mapSlotReplaceVariant = slotBase.extend({ action: z.literal("slot.replace"), slotId: z.string().max(200), ingredientEntryId: z.string().max(200), newIngredientEntryId: z.string().max(200) });
const mapSlotListVariant = slotBase.extend({ action: z.literal("slot.list") });
const versionBase = mapVersionSchema.omit({ action: true });
const mapVersionCommitVariant = versionBase.extend({ action: z.literal("version.commit") });
const mapVersionListVariant = versionBase.extend({ action: z.literal("version.list") });
const mapVersionHistoryVariant = versionBase.extend({ action: z.literal("version.history") });
const mapSuggestVariant = mapSuggestSchema.extend({ action: z.literal("suggest") });
const mapCreateAudienceSetVariant = createAudienceMapSetSchema.extend({ action: z.literal("create-audience-set") });

export const mapActionUnion = z.discriminatedUnion("action", [
  mapCreateVariant,
  mapGetVariant,
  mapListVariant,
  mapSlotAddVariant,
  mapSlotRemoveVariant,
  mapSlotReplaceVariant,
  mapSlotListVariant,
  mapVersionCommitVariant,
  mapVersionListVariant,
  mapVersionHistoryVariant,
  mapSuggestVariant,
  mapCreateAudienceSetVariant,
]);

/** §4 backpressure text — one entry per action, never used for registration. */
export const MAP_ACTION_SPECS: Record<MapAction, ActionParamSpec> = {
  create: { params: ["title", "templateId", "description", "slotIds"], description: "title is required." },
  get: { params: ["mapEntryId"], description: "mapEntryId is required." },
  list: { params: ["templateId", "status"], description: "All params optional." },
  "slot.add": { params: ["mapEntryId", "slotId", "ingredientEntryId", "label", "author"], description: "mapEntryId, slotId, and ingredientEntryId are required." },
  "slot.remove": { params: ["mapEntryId", "slotId", "ingredientEntryId", "author"], description: "mapEntryId, slotId, and ingredientEntryId are required." },
  "slot.replace": { params: ["mapEntryId", "slotId", "ingredientEntryId", "newIngredientEntryId", "label", "author"], description: "mapEntryId, slotId, ingredientEntryId, and newIngredientEntryId are required." },
  "slot.list": { params: ["mapEntryId", "slotId"], description: "mapEntryId is required." },
  "version.commit": { params: ["mapEntryId", "commitMessage", "author"], description: "mapEntryId is required." },
  "version.list": { params: ["mapEntryId"], description: "mapEntryId is required." },
  "version.history": { params: ["mapEntryId"], description: "mapEntryId is required." },
  suggest: { params: ["mapEntryId", "slotId", "query"], description: "mapEntryId is required." },
  "create-audience-set": { params: ["audienceEntryId"], description: "audienceEntryId is required." },
};

export function registerMapTools(server: McpServer) {
  // §3b handler map — strips the namespace prefix and reconstructs the
  // original legacy-schema action value before delegating to the shared
  // handler (handleMapCrud/handleMapSlot/handleMapVersion/handleMapSuggest/
  // handleCreateAudienceMapSet).
  const mapHandlers: Record<MapAction, (data: any) => Promise<ToolResult>> = {
    create: (data) => handleMapCrud({ ...data, action: "create" }),
    get: (data) => handleMapCrud({ ...data, action: "get" }),
    list: (data) => handleMapCrud({ ...data, action: "list" }),
    "slot.add": (data) => handleMapSlot({ ...data, action: "add" }),
    "slot.remove": (data) => handleMapSlot({ ...data, action: "remove" }),
    "slot.replace": (data) => handleMapSlot({ ...data, action: "replace" }),
    "slot.list": (data) => handleMapSlot({ ...data, action: "list" }),
    "version.commit": (data) => handleMapVersion({ ...data, action: "commit" }),
    "version.list": (data) => handleMapVersion({ ...data, action: "list" }),
    "version.history": (data) => handleMapVersion({ ...data, action: "history" }),
    suggest: (data) => handleMapSuggest(data),
    "create-audience-set": (data) => handleCreateAudienceMapSet(data),
  };

  const mapTool = server.registerTool(
    "map",
    {
      title: "Map",
      description:
        "Manage composed framework maps — CRUD, slot management, versioning, and suggestion. Maps assemble " +
        "ingredient entries into framework slots (e.g. Lean Canvas). Module-gated (PB_MODULES=gitchain).\n\n" +
        "- **create/get/list**: map CRUD.\n" +
        "- **slot.add/slot.remove/slot.replace/slot.list**: ingredient slot management (absorbs `map-slot`).\n" +
        "- **version.commit/version.list/version.history**: versioning (absorbs `map-version`).\n" +
        "- **suggest**: find ingredients to fill empty slots (absorbs `map-suggest`).\n" +
        "- **create-audience-set**: create Empathy/Narrowing/Jobs & Alternatives maps anchored to one audience entry (absorbs `create-audience-map-set`).",
      inputSchema: mapCompoundSchema,
      annotations: { readOnlyHint: false, destructiveHint: false, openWorldHint: false },
    },
    thinWrapper(async (args) => {
      const parsed = parseOrFail(mapCompoundSchema, args);
      if (!parsed.ok) return parsed.result;
      const { action } = parsed.data;

      return runWithToolContext({ tool: "map", action }, () =>
        dispatchDiscriminated("map", mapActionUnion, parsed.data, MAP_ACTION_SPECS, mapHandlers),
      );
    }),
  );
  trackWriteTool(mapTool);

  // WP-484 S3 (clean break, TEN-2157): the standalone `create-audience-map-set`,
  // `map-slot`, `map-version`, and `map-suggest` registrations retired here.
  // handleCreateAudienceMapSet / handleMapSlot / handleMapVersion /
  // handleMapSuggest stay live via `map` action=create-audience-set /
  // slot.* / version.* / suggest (mapHandlers, above).
}
